import { apiClientV2, unwrapV2Response } from './index.js';

/**
 * v2 Admin organization-management API.
 *
 * Wraps the `/v2/admin/organizations` endpoints on `apiClientV2`. All responses
 * use the v2 envelope `{ success, message, data, errors }`; each helper unwraps
 * `data` via `unwrapV2Response` and throws on `success: false`.
 *
 * These are ADMIN-only endpoints. The user-level self-service org page
 * (OrganizationProfile.jsx) deliberately keeps using the legacy v1
 * `organizations.js` wrappers — do not route it through here.
 *
 * Notable v2 changes vs v1:
 * - Org identified by `uuid`.
 * - List returns `items` (v1 returned `organizations`).
 * - Update is PATCH (partial) and no longer carries members or entitlements.
 * - Members are a sub-resource: PUT/DELETE `.../members/{user_uuid}`.
 * - Products are a sub-resource: PUT/DELETE `.../products/{product}`; toggling
 *   them is what drives the org's `entitlements`.
 */

const base = '/admin/organizations';

/**
 * Lists organizations (paginated). Maps the v2 `items` array to `organizations`
 * to match the existing table's expected shape during migration.
 *
 * @returns {Promise<{ organizations: object[], total: number, offset: number, limit: number }>}
 */
export const adminGetOrganizationsV2 = async ({
	offset = 0,
	limit = 100,
	search = '',
	deleted = false,
} = {}) => {
	const query = { offset, limit, deleted };
	if (search) query.search = search;

	const { data, error } = await apiClientV2.get(base, query);
	if (error) throw error;

	const page = unwrapV2Response(data);
	return {
		organizations: page?.items ?? [],
		total: page?.total ?? 0,
		offset: page?.offset ?? offset,
		limit: page?.limit ?? limit,
	};
};

/**
 * Fetches a single organization by uuid.
 *
 * @param {string} uuid
 * @returns {Promise<object>} OrgResponseSchema
 */
export const adminGetOrganizationV2 = async uuid => {
	const { data, error } = await apiClientV2.get(`${base}/${uuid}`);
	if (error) throw error;
	return unwrapV2Response(data);
};

/**
 * Creates an organization.
 *
 * @param {object} org - AdminOrgCreateRequest: { name*, description, org_no,
 *   country, city, address, postal_code, wms_workspace_name, project_tags[],
 *   members: [{ user_uuid, access[], role }] }
 * @returns {Promise<object>} the created org
 */
export const adminCreateOrganizationV2 = async org => {
	const { data, error } = await apiClientV2.post(base, org);
	if (error) throw error;
	return unwrapV2Response(data);
};

/**
 * Partially updates an organization's attributes. Does NOT handle members or
 * products (use the dedicated helpers below).
 *
 * @param {string} uuid
 * @param {object} updateData - AdminOrgUpdateRequest (any subset of: name,
 *   description, org_no, country, city, address, postal_code,
 *   wms_workspace_name, project_tags[])
 * @returns {Promise<object>} the updated org
 */
export const adminUpdateOrganizationV2 = async (uuid, updateData) => {
	const { data, error } = await apiClientV2.patch(`${base}/${uuid}`, updateData);
	if (error) throw error;
	return unwrapV2Response(data);
};

/**
 * Soft-deletes (archives) an organization.
 *
 * @param {string} uuid
 */
export const adminDeleteOrganizationV2 = async uuid => {
	const { data, error } = await apiClientV2.delete(`${base}/${uuid}`);
	if (error) throw error;
	return unwrapV2Response(data);
};

/**
 * Adds or updates a member on an organization. Returns the updated org.
 *
 * @param {string} uuid - org uuid
 * @param {string} userUuid - member user uuid
 * @param {{ access: string[], role: string }} member - access tokens + role
 * @returns {Promise<object>} the updated org
 */
export const adminPutOrganizationMemberV2 = async (uuid, userUuid, member) => {
	const { data, error } = await apiClientV2.put(
		`${base}/${uuid}/members/${userUuid}`,
		member
	);
	if (error) throw error;
	return unwrapV2Response(data);
};

/**
 * Removes a member from an organization. Returns the updated org.
 *
 * @param {string} uuid - org uuid
 * @param {string} userUuid - member user uuid
 * @returns {Promise<object>} the updated org
 */
export const adminDeleteOrganizationMemberV2 = async (uuid, userUuid) => {
	const { data, error } = await apiClientV2.delete(
		`${base}/${uuid}/members/${userUuid}`
	);
	if (error) throw error;
	return unwrapV2Response(data);
};

/**
 * Enables a product on an organization (updates entitlements server-side).
 * Returns the updated org.
 *
 * @param {string} uuid - org uuid
 * @param {string} product - a ProductEnum value (e.g. 'product.dsp')
 * @returns {Promise<object>} the updated org
 */
export const adminEnableOrganizationProductV2 = async (uuid, product) => {
	const { data, error } = await apiClientV2.put(
		`${base}/${uuid}/products/${product}`
	);
	if (error) throw error;
	return unwrapV2Response(data);
};

/**
 * Disables a product on an organization (updates entitlements server-side).
 * Returns the updated org.
 *
 * @param {string} uuid - org uuid
 * @param {string} product - a ProductEnum value (e.g. 'product.dsp')
 * @returns {Promise<object>} the updated org
 */
export const adminDisableOrganizationProductV2 = async (uuid, product) => {
	const { data, error } = await apiClientV2.delete(
		`${base}/${uuid}/products/${product}`
	);
	if (error) throw error;
	return unwrapV2Response(data);
};
